import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import Server from '@/plugins/Server'

// --- TypeScript Interfaces ---

export interface PriceOption {
  label: string
  min: number
  max: number
}

export interface Filters {
  sort_type: string | null
  page_limit: number
  search_input: string
  manufacturers: number[] | string[]
  device_types: string[]
  copier_colors: string[]
  speed_minimum: number | null
  speed_maximum: number | null
  active_speed?: string | null
  price_filters: any[]
  price_options: PriceOption[]
  paper_sizes: string[]
  product_categories: string[]
  form_factors: string[]
  capabilities: string[]
  connection_types: string[]
  usage_levels: string[]
}

export interface Snackbar {
  visible: boolean
  content: string
  icon?: string
  type?: 'success' | 'error' | 'info' | 'warning'
}

export interface Dialog {
  visible: boolean
  title?: string
  content?: string
}

export interface MenuItem {
  title: string
  value: string
  step: number
}

export interface CopierType {
  value: string | null
  title: string
  icon: string
}

export interface FormAddress {
  street: string
  city: string
  state: string
  zip: string
}

export interface FormLead {
  company: string
  firstname: string
  lastname: string
  email: string
  phone: string
}

export interface FormCopier {
  lease_term: number
  copier_type: string | null
  copier_quantity: number
  prints_per_month: number
  num_mails_per_month: number
  owns_mailing_machine: boolean | null | string
  how_many_rolls: number | null | string
  need_a_scanner: boolean | null | string
  type_of_printing: string[]
  printing_dims: string | null
  acquisition_type: 'lease' | 'purchase'
}

export interface FormPhone {
  phone_sys_reason: string | null
  phone_quantity: string | number | null
}

export interface Form {
  address: FormAddress
  lead: FormLead
  copier: FormCopier
  phone: FormPhone
}

export interface AssistantChat {
  id?: number | null
  role: 'ai' | 'human' | 'system'
  message: string
  timestamp?: string | null
}

export interface Assistant {
  chats: AssistantChat[]
  prompt: string
  config: any
  theme: string
  visible: boolean
}

// --- Store Definition ---

export const useGlobalsStore = defineStore('globals', () => {
  
  // --- State ---
  
  const is_fetching = ref(false)
  
  const filters = ref<Filters>({
    sort_type: null,
    page_limit: 16,
    search_input: '',
    manufacturers: [],
    device_types: [],
    copier_colors: [],
    speed_minimum: null,
    speed_maximum: null,
    price_filters: [],
    price_options: [
      { label: "$69-$99", min: 69, max: 99 },
      { label: "$100-$199", min: 100, max: 199 },
      { label: "$200+", min: 200, max: 9999 }
    ],
    paper_sizes: [],
    product_categories: [],
    form_factors: [],
    capabilities: [],
    connection_types: [],
    usage_levels: [],
  })

  const snackbar = ref<Snackbar>({
    visible: false,
    content: '',
    icon: '',
    type: 'success',
  })

  const dialog = ref<Dialog>({
    visible: false,
    title: '',
    content: '',
  })

  const sort_types = ref([
    { value: 'ascending', title: 'Sort: Name (A-Z)' },
    { value: 'descending', title: 'Sort: Name (Z-A)' },
    { value: 'speed-low-high', title: 'Sort: Speed (Low to High)' },
    { value: 'speed-high-low', title: 'Sort: Speed (High to Low)' },
    { value: 'newest', title: 'Sort: Newest First' },
    { value: 'oldest', title: 'Sort: Oldest First' },
    { value: null, title: 'Sort: Default Order' }
  ])

  const menu_items = ref<MenuItem[]>([
    { title: 'Choose a copier type', value: 'client_dashboard', step: 1 },
    { title: 'Estimated monthly prints', value: 'client_contracts', step: 2 },
    { title: 'Configure your copier', value: 'client_agents', step: 3 },
    { title: 'Finalize your Quote', value: 'client_agents', step: 4 },
  ])

  const copier_types = ref<CopierType[]>([
    { value: 'B&W', title: 'Black & White', icon: 'mdi-invert-colors-off' },
    { value: 'Color', title: 'Color', icon: 'mdi-invert-colors' },
    { value: null, title: 'Not Sure', icon: 'mdi-help-circle' }
  ])

  const datasets = ref<any[]>([])
  const accessories = ref<any[]>([])
  const categories = ref<any[]>([])
  const lease_terms = ref<any[]>([])
  const product_pricing = ref<any[]>([])
  const cart_items = ref<any[]>([])
  const selected_copier = ref<any | null>(null)

  const form = ref<Form>({
    address: { street: '', city: '', state: '', zip: '' },
    lead: { company: '', firstname: '', lastname: '', email: '', phone: '' },
    copier: {
      lease_term: 36,
      copier_type: null,
      copier_quantity: 0,
      prints_per_month: 500,
      num_mails_per_month: 0,
      owns_mailing_machine: null,
      how_many_rolls: null,
      need_a_scanner: null,
      type_of_printing: [],
      printing_dims: null,
      acquisition_type: 'lease',
    },
    phone: { phone_sys_reason: null, phone_quantity: null }
  })

  const products = ref<any[]>([])
  const manufacturers = ref<any[]>([])
  const product_reviews = ref<any[]>([])

  const printer_categories = ref<any[]>([])
  const paper_sizes = ref<any[]>([])
  const connection_types = ref<any[]>([])
  const media_types = ref<any[]>([])
  const form_factors = ref<any[]>([])
  const capabilities = ref<any[]>([])
  const usage_levels = ref<any[]>([])

  const configurator = ref(false)
  const config_drawer = ref(true)
  const config_fullscreen = ref(false)
  const view_product = ref<any | null>(null)
  const zoom_level = ref(1)
  const redo_steps = ref<any[]>([])

  const cart_modal = ref(false)
  const form_modal = ref(false)
  const form_success = ref(false)
  const review_modal = ref(false)
  const active_form = ref<string | null>(null)
  const is_loading = ref(false)
  const addtocart_trigger = ref(0)
  
  const states = ref([
    "AL", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA",
    "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA",
    "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM",
    "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD",
    "TN", "TX", "UT", "VT", "VI", "VA", "WA", "WV", "WI", "WY",
  ])

  const manufacturers_allowed = ref<number[]>([])
  const free_shipping = ref(false)
  const catalog_page_title = ref("Products Catalog")
  const is_mobile = ref(false)
  
  const assistant = ref<Assistant>({
    chats: [],
    prompt: "",
    config: null,
    theme: "light",
    visible: true,
  })

  const form_step = ref(1)
  const color_scheme = ref('light')

  // --- Dynamic Refs Map for SetAnyState Mutation Mirror ---

  const storeRefsMap: Record<string, any> = {
    is_fetching,
    filters,
    snackbar,
    dialog,
    datasets,
    accessories,
    categories,
    lease_terms,
    product_pricing,
    cart_items,
    selected_copier,
    form,
    products,
    manufacturers,
    product_reviews,
    printer_categories,
    paper_sizes,
    connection_types,
    media_types,
    form_factors,
    capabilities,
    usage_levels,
    configurator,
    config_drawer,
    config_fullscreen,
    view_product,
    zoom_level,
    redo_steps,
    cart_modal,
    form_modal,
    form_success,
    review_modal,
    active_form,
    is_loading,
    addtocart_trigger,
    manufacturers_allowed,
    free_shipping,
    catalog_page_title,
    is_mobile,
    assistant,
    form_step,
    color_scheme
  }

  // --- Actions ---

  async function FetchCatalogData() {
    is_fetching.value = true
    try {
      const res = await Server.get('/quotetool/fetch-data')
      if (res.data.result) {
        const settings = window.catalog_config
        if (settings) {
          free_shipping.value = settings.free_shipping
          manufacturers_allowed.value = settings.manufacturers_allowed
          catalog_page_title.value = settings.catalog_page_title
          
          const select_products: any[] = []
          if (res.data.products) {
            res.data.products.forEach((product: any) => {
              if (settings.manufacturers_allowed.includes(product.manufacturer_id)) {
                if (!settings.product_exclusions.includes(product.id)) {
                  select_products.push({
                    ...product,
                    best_seller: settings.best_sellers.includes(product.id)
                  })
                }
              }
            })
          }
          products.value = select_products

          // Update page_limit filters
          filters.value.page_limit = Number(settings.items_per_page) || 16
        } else {
          // Fallback if settings are not defined
          products.value = res.data.products || []
        }

        manufacturers.value = res.data.manufacturers || []
        categories.value = res.data.categories || []
        accessories.value = res.data.accessories || []
        lease_terms.value = res.data.lease_terms || []
        product_pricing.value = res.data.product_pricing || []
        product_reviews.value = res.data.product_reviews || []

        printer_categories.value = res.data.printer_categories || []
        paper_sizes.value = res.data.paper_sizes || []
        connection_types.value = res.data.connection_types || []
        media_types.value = res.data.media_types || []
        form_factors.value = res.data.form_factors || []
        capabilities.value = res.data.capabilities || []
        usage_levels.value = res.data.usage_levels || []

        const new_datasets: any[] = []
        if (res.data.datasets) {
          res.data.datasets.forEach((dataset: any) => {
            new_datasets.push({
              ...dataset,
              selected: dataset.default_attached == 1
            })
          })
        }
        datasets.value = new_datasets
      } else {
        SetSnackbar({
          visible: true,
          content: res.data.errors || 'Unknown error occurred.',
          type: 'error',
          icon: 'mdi-alert-circle-outline'
        })
      }
    } catch (err: any) {
      SetError(err.message || 'An error occurred while fetching catalog data.')
    } finally {
      is_fetching.value = false
    }
  }

  function SetFilters(params: Filters) {
    filters.value = params
  }

  function SetFormStep(action: number | 'prev' | 'next') {
    if (typeof action === 'number') {
      form_step.value = action
    } else if (action === 'prev') {
      if (form_step.value > 1) {
        form_step.value--
      }
    } else {
      form_step.value++
    }
  }

  function SetCartItems(items: any[]) {
    cart_items.value = items
  }

  function SetSelectedCopier(copier: any) {
    selected_copier.value = copier
  }

  function SetSnackbar(params: Snackbar) {
    snackbar.value = params
  }

  function SetDialog(params: Dialog) {
    dialog.value = params
  }

  function SetError(message: string) {
    SetSnackbar({
      visible: true,
      content: message,
      type: 'error',
      icon: 'mdi-alert-circle-outline'
    })
  }

  function SetSuccess(message: string) {
    SetSnackbar({
      visible: true,
      content: message,
      type: 'success',
      icon: 'mdi-check-all'
    })
  }

  function SetAnyState({ target, value }: { target: string; value: any }) {
    if (target in storeRefsMap) {
      storeRefsMap[target].value = value
    }
  }

  function ToggleChatUI() {
    assistant.value.visible = !assistant.value.visible
  }

  function AddChat(chat: AssistantChat) {
    assistant.value.chats.push(chat)
  }

  function ClearQuoteValues() {
    form.value.copier = {
      lease_term: 36,
      copier_type: null,
      copier_quantity: 0,
      prints_per_month: 500,
      num_mails_per_month: 0,
      owns_mailing_machine: null,
      how_many_rolls: null,
      need_a_scanner: null,
      type_of_printing: [],
      printing_dims: null,
      acquisition_type: 'lease',
    }
    form.value.phone = {
      phone_sys_reason: null,
      phone_quantity: null,
    }
  }

  // --- Ported from CommonMixins.js ---

  const ConfigShopURL = computed(() => 'https://1800toners.com')

  const ConfigTopbarColor = computed(() => {
    return window.catalog_config?.colors?.topbar || '#ffffff'
  })

  const ConfigShowPricing = computed(() => {
    return window.catalog_config?.show_pricing ?? false
  })

  const ConfigModalsColor = computed(() => {
    return window.catalog_config?.colors?.modals || '#1565C0'
  })

  const ConfigSinglePageUrl = computed(() => {
    const url = window.catalog_config?.single_page_url || '/product'
    return url.replace(/\/$/, '')
  })

  const ConfigElementsColor = computed(() => {
    return window.catalog_config?.colors?.buttons || '#1565C0'
  })

  const ConfigShowShopBanner = computed(() => {
    return window.catalog_config?.show_shop_banner ?? false
  })

  const ConfigShowFeaturedCards = computed(() => {
    return window.catalog_config?.show_featured_cards ?? false
  })

  const ConfigChatEnabled = computed(() => {
    return !!(assistant.value.config && assistant.value.config.enabled)
  })

  function SetState(target: string, value: any) {
    SetAnyState({ target, value })
  }

  function OpenHelpWindow() {
    if (ConfigChatEnabled.value) {
      ToggleChatUI()
    }
  }

  function FormSelector() {
    let the_copier = null
    if (selected_copier.value) {
      the_copier = selected_copier.value
    } else if (cart_items.value.length > 0) {
      the_copier = cart_items.value[cart_items.value.length - 1].product
    }
    
    if (the_copier) {
      if (the_copier.manufacturer_id == 9) {
        SetState('active_form', 'mailing-form')
      } else if (the_copier.form_factor.toLowerCase() == 'wide-format') {
        SetState('active_form', 'wide-format-form')
      } else if (the_copier.printer_category.toLowerCase().includes('phone')) {
        SetState('active_form', 'phone-form')
      } else if (the_copier.printer_category.toLowerCase() == 'scanner') {
        SetState('active_form', 'scanner-form')
      } else {
        SetState('active_form', 'default-form')
      }
    }
  }

  function SetAiAssistantConfig() {
    const AiAssistant = { ...assistant.value }
    AiAssistant.config = window.ai_chat_config
    AiAssistant.visible = false
    SetState('assistant', AiAssistant)
  }

  function ProductImage(product: any) {
    const url = (import.meta.env.VITE_SERVERAPI_URL || '') + '/storage/uploads/products'
    if (product?.product_image != null && product.product_image.length > 0) {
      return `${url}/product-img/${product.product_image}`
    } else {
      return `${url}/product-img/_placeholder_.png`
    }
  }

  return {
    // State
    is_fetching,
    filters,
    snackbar,
    dialog,
    sort_types,
    menu_items,
    copier_types,
    datasets,
    accessories,
    categories,
    lease_terms,
    product_pricing,
    cart_items,
    selected_copier,
    form,
    products,
    manufacturers,
    product_reviews,
    printer_categories,
    paper_sizes,
    connection_types,
    media_types,
    form_factors,
    capabilities,
    usage_levels,
    configurator,
    config_drawer,
    config_fullscreen,
    view_product,
    zoom_level,
    redo_steps,
    cart_modal,
    form_modal,
    form_success,
    review_modal,
    active_form,
    is_loading,
    addtocart_trigger,
    states,
    manufacturers_allowed,
    free_shipping,
    catalog_page_title,
    is_mobile,
    assistant,
    form_step,
    color_scheme,

    // Actions
    FetchCatalogData,
    SetFilters,
    SetFormStep,
    SetCartItems,
    SetSelectedCopier,
    SetSnackbar,
    SetDialog,
    SetError,
    SetSuccess,
    SetAnyState,
    ToggleChatUI,
    AddChat,
    ClearQuoteValues,

    // Configs & Mixin helpers
    ConfigShopURL,
    ConfigTopbarColor,
    ConfigShowPricing,
    ConfigModalsColor,
    ConfigSinglePageUrl,
    ConfigElementsColor,
    ConfigShowShopBanner,
    ConfigShowFeaturedCards,
    ConfigChatEnabled,
    SetState,
    OpenHelpWindow,
    FormSelector,
    SetAiAssistantConfig,
    ProductImage
  }
}, {
  persist: {
    pick: [
      'form',
      'filters',
      'assistant',
      'cart_items',
      'configurator',
      'color_scheme',
      'selected_copier'
    ]
  }
})
